import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CheckCircle, ArrowRight, Zap, Globe, Users } from 'lucide-react';

interface WelcomeStepProps {
  onNext: () => void;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onNext }) => {
  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Get your first translation live in under 5 minutes',
      bg: 'bg-blue-50',
      color: 'text-blue-600'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Support for 100+ languages out of the box',
      bg: 'bg-purple-50',
      color: 'text-purple-600'
    },
    {
      icon: Users,
      title: 'Team Ready',
      description: 'Real-time collaboration with your team',
      bg: 'bg-green-50',
      color: 'text-green-600'
    }
  ];

  const steps = [
    { title: 'Create your first site', description: 'Set up a website or app for localization' },
    { title: 'Choose target languages', description: 'Select which languages you want to support' },
    { title: 'Install integration', description: 'Connect via SDK, Shopify, WooCommerce, or API' },
    { title: 'See your first translation', description: 'Watch the magic happen in real-time' }
  ];

  return (
    <CardContent className="p-12 text-center">
      {/* Hero Icon */}
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-primary to-primary/70 rounded-full text-white text-5xl">
          🚀
        </div>
      </div>

      {/* Title */}
      <h1 className="text-4xl font-bold text-foreground mb-4">
        Welcome to LocalizationHub!
      </h1>

      <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
        Let's get you set up in less than 5 minutes. We'll help you create your
        first site and start translating content.
      </p>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
        {features.map((feature, index) => (
          <Card key={index} className={`${feature.bg} border-0`}>
            <CardContent className="p-6 text-center">
              <feature.icon className={`h-10 w-10 ${feature.color} mx-auto mb-3`} />
              <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* What We'll Do */}
      <Card className="bg-muted/30 border-0 mb-8 max-w-2xl mx-auto">
        <CardContent className="p-6">
          <h3 className="font-semibold text-foreground mb-4 text-center">
            What we'll cover:
          </h3>
          <div className="space-y-3 text-left">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTA Button */}
      <Button
        onClick={onNext}
        size="lg"
        className="text-lg px-8 py-6 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg"
      >
        Let's Get Started
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>

      <p className="mt-6 text-sm text-muted-foreground">
        Takes about 5 minutes • You can skip and come back later
      </p>
    </CardContent>
  );
};

export default WelcomeStep;
